import React, { useEffect, useMemo, useState } from 'react';
import { useWizard } from '../../wizard';
import { StepContainer, NavigationButtons, InfoBox } from '../index';
import { SummaryList, type SummaryListItem } from '../SummaryList';
import { contentApi } from '../../services/contentApi';
import { contentKeys } from '../../constants/contentKeys';

const damageLabels: Record<string, string> = {
  intact: 'Nej, det er ikke beskadiget',
  unreadable: 'Passet indeholder ulæselige stempler/foto/tekst',
  bent: 'Passet er bøjet',
  missingPages: 'Passet mangler sider',
  worn: 'Passet er overdrevet slidt',
  other: 'Andet'
};

const photoMethodLabels: Record<string, string> = {
  borgerservice: 'Foto tages i Borgerservice',
  egen: 'Medbringer eget pasfoto'
};

const formatDate = (value?: string): string => {
  if (!value) {
    return '';
  }
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) {
    return value;
  }
  const monthNames = ['januar', 'februar', 'marts', 'april', 'maj', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'december'];
  const monthLabel = monthNames[month - 1];
  return `${day}. ${monthLabel} ${year}`;
};

const formatTime = (value?: string): string => {
  return value ? `kl. ${value}` : '';
};

const SummaryStep: React.FC = () => {
  const { selfServiceData, goNext, goBack } = useWizard();
  const [infoBody, setInfoBody] = useState<string[] | null>(null);

  useEffect(() => {
    contentApi
      .getByKey(contentKeys.pas.summary)
      .then((content) => {
        if (content?.body && content.body.length > 0) {
          setInfoBody(content.body);
        }
      })
      .catch(() => undefined);
  }, []);

  const {
    passportFor,
    hasOldPassport,
    passportDamage,
    previousCitizenship,
    addressingName,
    custodyBothPresent,
    medindehaverName,
    medindehaverEmail,
    passportHeight,
    locationName,
    date,
    time,
    contactName,
    contactMobil,
    contactEmail,
    photoMethod
  } = selfServiceData;

  const passportForText = useMemo(() => {
    if (passportFor === 'self') {
      return 'Mig selv';
    }
    if (passportFor === 'child') {
      return 'Et barn';
    }
    return null;
  }, [passportFor]);

  const summaryItems = useMemo<SummaryListItem[]>(() => {
    const items: SummaryListItem[] = [];

    if (passportForText) {
      items.push({ key: 'Ansøgning for', value: passportForText });
    }

    items.push({
      key: 'Medbringer gammelt pas',
      value: hasOldPassport ? 'Ja' : 'Nej (bortkommet)'
    });

    if (hasOldPassport && passportDamage) {
      items.push({
        key: 'Beskadiget',
        value: damageLabels[passportDamage] ?? 'Andet'
      });
    }

    if (addressingName) {
      items.push({ key: 'Adresseringsnavn', value: addressingName });
    }

    if (passportFor === 'child') {
      items.push({
        key: 'Forældremyndighed',
        value: custodyBothPresent ? 'Begge møder' : 'Samtykke indhentes'
      });

      if (medindehaverName) {
        const medindehaverValue = medindehaverEmail ? `${medindehaverName} (${medindehaverEmail})` : medindehaverName;
        items.push({ key: 'Medindehaver', value: medindehaverValue });
      }
    }

    items.push({
      key: 'Tidl. statsborgerskab',
      value: previousCitizenship ? 'Ja' : 'Nej'
    });

    if (typeof passportHeight === 'number') {
      items.push({ key: 'Højde', value: `${passportHeight} cm` });
    }

    if (photoMethod) {
      items.push({
        key: 'Pasfoto',
        value: photoMethodLabels[photoMethod] ?? photoMethod
      });
    }

    const contactParts = [contactName, contactEmail, contactMobil].filter(Boolean);
    if (contactParts.length > 0) {
      items.push({
        key: 'Kontaktoplysninger',
        value: (
          <span>
            {contactName && <span>{contactName}<br /></span>}
            {contactEmail && <span>{contactEmail}<br /></span>}
            {contactMobil && <span>{contactMobil}</span>}
          </span>
        )
      });
    }

    items.push({ key: 'Sted', value: locationName || '' });
    items.push({ key: 'Dato', value: formatDate(date) });
    items.push({ key: 'Tid', value: formatTime(time) });

    return items;
  }, [
    passportFor,
    passportForText,
    hasOldPassport,
    passportDamage,
    addressingName,
    custodyBothPresent,
    medindehaverName,
    medindehaverEmail,
    previousCitizenship,
    passportHeight,
    photoMethod,
    contactName,
    contactEmail,
    contactMobil,
    locationName,
    date,
    time
  ]);

  const reminders = useMemo(() => {
    const items: string[] = [];
    if (hasOldPassport) {
      items.push('Dit seneste pas');
    } else {
      items.push('Anden billedlegitimation (fx kørekort)');
    }
    if (previousCitizenship) {
      items.push('Dokumentation for dansk statsborgerskab');
    }
    if (passportFor === 'child') {
      items.push('Barnets dåbs- eller navneattest');
    }
    return items;
  }, [hasOldPassport, previousCitizenship, passportFor]);

  return (
    <StepContainer title="Oversigt over din bestilling">
      {infoBody && (
        <InfoBox type="info">
          {infoBody.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </InfoBox>
      )}

      <SummaryList items={summaryItems} />

      {reminders.length > 0 && (
        <div className="info-box" role="note">
          <strong>Husk at medbringe:</strong>
          <ul>
            {reminders.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      )}

      <NavigationButtons onBack={goBack} onNext={goNext} />
    </StepContainer>
  );
};

export default SummaryStep;
